import React, { useEffect, useRef, useState } from 'react';
import Breadcrumbs, { IBreadcrumbsLink } from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Link from '@mui/material/Link';
import { Box } from '@mui/system';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import {
  EDictionaryPrefixTypesUniqueId,
  findInDictionary,
  mapDictionaryToOptionProp,
} from 'helpers/dictionary';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { Accordion } from 'components/molecules/Accordion';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Stack, Typography } from '@mui/material';
import { StyledDetailsWrapper } from 'pages/Firearms/RegisteredFirearmDeleteCommentPage/styles';
import { getLegalEntity, getLegalType, getOrgLegalEntity } from 'requests/legalEntities';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { Loader } from 'components/atoms/Loader';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { colors } from 'theme/colors';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EIconTypes } from 'constants/Icons';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { FormInput } from 'components/molecules/FormInput';
import {
  StyledIconWrapper,
  StyledLocationMarkConfirmation,
} from 'pages/LegalEntitiesPage/LegalEntityAddTransactionForGroupPage/styles';
import Icon from 'components/atoms/Icon/Icon';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDictionary } from 'models/dictionaryContext';
import { EDictionaryTypes, IDictionary, ITranslation } from 'models/dictionary';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { EMIMETypes } from 'constants/MIMETypes';
import {
  createMiddleTransaction,
  createMiddleTransactionForGroup,
  getFirearmLastTransaction,
  getGroupLastTransaction,
  getPossibleTransactionType,
  getPossibleTransactionTypeForGroup,
} from 'requests/transaction';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { formatDate, formatDateTimeToIso } from 'utils/date';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import {
  getFirearmGroupDetails,
  getGroupLastTransactionUniqueId,
  getLastFirearmTransactionUniqueId,
  getRegisteredFirearm,
} from 'requests/firearm';
import { isPathEqualToRouteName } from 'utils/route';
import { IRegisteredFirearmData } from 'models/registeredFirearm';
import { EFirearmStateId, IFirearm, IFirearmGroup } from 'models/firearm';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import { useFRT } from 'pages/FRT/hooks';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useScroll } from 'hooks/useScroll';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useDocumentsGeneratingSetup } from 'pages/SystemSettingsPage/DocumentsGeneratingSetupPage/hooks';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { removeFileExtension } from 'utils/files';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { typesWithoutStrictAddressLocation } from 'utils/transactionUtils';
import { FormModal } from 'components/molecules/Modal';
import { getAttachmentError } from 'helpers/errors';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { isEmpty } from 'utils/search';
import { SelectLocationTransactionPage } from 'pages/Transactions/SelectLocationTransactionPage';
import { MarkingsFormForMarked } from 'components/organisms/MarkingsFormForMarked';

const MAX_FILE_SIZE_IN_MB = 3;

let DISABLED_FOR_TRANSACTION_FORM = [ETransactionTypes.DISABLED];

const TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS = [
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
];

const CHANGE_LE_REQUIRED_TRANSACTION_TYPES = [
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.SOLD,
  ETransactionTypes.GIVEN,
  ETransactionTypes.LEASED,
  ETransactionTypes.ASSIGNED_TEMPORARILY,
  ETransactionTypes.ASSIGNED_INDEFINITELY,
  ETransactionTypes.TRANSFERRED,
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.RETURNED,
  ETransactionTypes.SURPLUS_SALE,
];

export enum LETypes {
  individual = 'INDIVIDUAL',
  organization = 'ORGANIZATION',
}

export enum MarkingTypes {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
  DEACTIVATION = 'DEACTIVATION',
  CONFISCATION = 'CONFISCATION',
  SURPLUS_SALE = 'SURPLUS_SALE',
  APPLIED = 'APPLIED',
}

const getMarkingType = (typeUniqueId: string, markingType?: string) => {
  switch (typeUniqueId) {
    case ETransactionTypes.EXPORTED:
      return MarkingTypes.EXPORT;
    case ETransactionTypes.DEACTIVATED:
      return MarkingTypes.DEACTIVATION;
    case ETransactionTypes.MARKED:
      if (markingType === MarkingTypes.IMPORT) {
        return MarkingTypes.IMPORT;
      } else {
        return MarkingTypes.APPLIED;
      }
    case ETransactionTypes.CONFISCATED:
      return MarkingTypes.CONFISCATION;
    case ETransactionTypes.SURPLUS_SALE:
      return MarkingTypes.SURPLUS_SALE;
    case ETransactionTypes.DECLARED_IMPORTED:
      return MarkingTypes.IMPORT;
    default:
      break;
  }
};

const filterDisabled = (
  transactions: IDictionary[] | null,
  legalEntityIndividualKeeper: ILegalEntityData | null | undefined,
): IDictionary[] | null => {
  if (transactions === null) {
    return null;
  }
  if (legalEntityIndividualKeeper !== null && legalEntityIndividualKeeper !== undefined) {
    DISABLED_FOR_TRANSACTION_FORM = [ETransactionTypes.DISABLED];
  } else {
    DISABLED_FOR_TRANSACTION_FORM = [ETransactionTypes.DISABLED, ETransactionTypes.GIVEN];
  }
  return transactions.filter(
    (transaction) =>
      !DISABLED_FOR_TRANSACTION_FORM.map((disabled) => disabled.toString()).includes(
        transaction.uniqueId.toString(),
      ),
  );
};

const LegalEntityAddTransactionForGroupPage = () => {
  const navigate = useNavigate();
  const { state: routerState, pathname }: any = useLocation();
  const { loadInitData, eTraceEnabled, iArmsEnabled } = useDocumentsGeneratingSetup();
  const { id = '', groupId = '' } = useParams<{
    id: string;
    groupId: string;
    isTransactionForGroup: string;
  }>();
  const { elRef, scrollToError } = useScroll();

  const { firearm = {} } = routerState || {};
  const isTransactionForGroup = groupId === '' ? '0' : '1';
  const isMiddleTransactionPage = isPathEqualToRouteName(
    pathname,
    ERouteLinks.MiddleTransaction,
  );
  const isGroupTransactionPage = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityAddTransactionForGroup,
  );

  const { shortDateFormat } = useGlobalProperty();
  const { getFRTDetailsData, FRTDetails } = useFRT();

  const [frtTitleDetails, setFrtTitleDetails] = useState<string>('');
  const [frtUrlDetails, setFrtUrlDetails] = useState<string>();
  const [isFrtInformationVisible, setIsFrtInformationVisible] = useState<boolean>(false);
  const [firearmGroup, setFirearmGroup] = useState<IFirearmGroup | null>(null);
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const [registeredFirearm, setRegisteredFirearm] = useState<IRegisteredFirearmData | null>(
    null,
  );
  const [lastTransactionUniqueId, setLastTransactionUniqueId] = useState<string | null>();
  const [type, setType] = useState<'INDIVIDUAL' | 'ORGANIZATION' | null>(null);

  const [newFirearmLocationOwner, setNewFirearmLocationOwner] = useState<string | null>(null);
  const [newFirearmLocationOwnerInserted, setNewFirearmLocationOwnerInserted] =
    useState(false);

  const initialFormValues = {
    typeUniqueId: routerState?.typeUniqueId || '',
    transactionDate: routerState?.transactionDate || '',
    firearmsQuantity: routerState?.firearmsQuantity || '',
    serialNumber: routerState?.serialNumber || '',
    appliedMarking: routerState?.appliedMarking || '',
    manufacturedAt: routerState?.manufacturedAt || '',
    countryOfImportUniqueId: routerState?.countryOfImportUniqueId || '',
    importer: routerState?.importer || '',
    countryOfExportUniqueId: routerState?.countryOfExportUniqueId || '',
    expirationDate: routerState?.expirationDate || '',
    expirationTime: routerState?.expirationTime || '',
    attachments: routerState?.attachments?.length ? routerState?.attachments : [],
    legalEntityFullName: routerState?.legalEntityFullName || '',
    legalEntityId: routerState?.legalEntityId || '',
    description: routerState?.description || '',
    address: routerState?.address || '',
    unassignedLocation: routerState?.unassignedLocation || false,
    latitude: routerState?.coords?.[0],
    longitude: routerState?.coords?.[1],
    addressId: routerState?.addressId || null,
    countryUniqueId: routerState?.countryUniqueId || undefined,
    city: routerState?.city || undefined,
    marking: routerState?.marking || undefined,
    markings: routerState?.markings || {},
    additionalData: routerState?.additionalData || {},
    additionalDataMap: routerState?.additionalDataMap || {},
  };

  const { t } = useTranslations();

  const yearSchema = yup
    .string()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
    .nullable()
    .notRequired();

  const schema = yup.object().shape({
    typeUniqueId: yup.string().required(t('general.field.validation.required')),
    attachments: yup.array().of(
      yup.object({
        attachmentName: yup
          .string()
          .test('unique', t('general.field.validation.unique'), (value, params) => {
            // @ts-expect-error: required by update
            const attachments = params?.from[1]?.value.attachments ?? [];
            if (!value) return true;
            if (attachments.filter((r: any) => r.attachmentName === value).length > 1) {
              return params.createError({
                path: params.path,
                message: t('general.field.validation.unique'),
              });
            }
            return true;
          })
          .required(t('general.field.validation.required'))
          .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
          .matches(/^[a-zA-Z0-9_ -]+$/, t('general.field.validation.attachmentName'))
          .trim(),
      }),
    ),
    transactionDate: yup
      .string()
      .required(t('general.field.validation.required'))
      .test(
        'date-validation',
        t('legalEntity.validation.maxDate').replace('{0}', '255'),
        (value) => {
          if (typeof value !== 'string') {
            return false;
          }
          const maxValidDate = new Date();
          const passedDate = new Date(value);
          return passedDate.valueOf() <= maxValidDate.valueOf();
        },
      ),
    legalEntityFullName: yup.string().when('typeUniqueId', {
      is: (typeVar: any) => [...CHANGE_LE_REQUIRED_TRANSACTION_TYPES].includes(typeVar),
      then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
    }),
    countryOfImportUniqueId: yup.string().when('typeUniqueId', {
      is: ETransactionTypes.DECLARED_IMPORTED,
      then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
    }),
    countryOfExportUniqueId: yup.string().when('typeUniqueId', {
      is: ETransactionTypes.EXPORTED,
      then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
    }),
    description: yup
      .string()
      .test(
        'editor-validation',
        t('general.field.validation.maxCharacters').replace('{0}', '1000'),
        (value) => countEditorCharacters(value) <= 1000,
      )
      .when('typeUniqueId', {
        is: ETransactionTypes.MODIFIED,
        then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
      }),
    importer: yup
      .string()
      .max(250, t('general.field.validation.maxCharacters').replace('{0}', '250')),
    expirationTime: yup
      .string()
      .typeError(t('general.field.validation.required'))
      .when('typeUniqueId', {
        is: (typeVar: any) =>
          [ETransactionTypes.ASSIGNED_TEMPORARILY, ETransactionTypes.LEASED].includes(typeVar),
        then: (yupSchema) =>
          yupSchema
            .required(t('general.field.validation.required'))
            .test(
              'expiration-before-transaction-date-validation',
              t('legalEntity.validation.expirationDateValidation').replace('{0}', '255'),
              (value, context) => {
                if (typeof value !== 'string') {
                  return false;
                }
                return (
                  new Date(value).valueOf() >=
                  new Date(context.parent.transactionDate).valueOf()
                );
              },
            ),
      }),
    expirationDate: yup
      .string()
      .typeError(t('general.field.validation.required'))
      .when('typeUniqueId', {
        is: (typeVar: any) => [ETransactionTypes.TEST_FIRED].includes(typeVar),
        then: (yupSchema) =>
          yupSchema.test(
            'expiration-before-transaction-date-validation',
            t('legalEntity.validation.expirationDateValidation').replace('{0}', '255'),
            (value, context) => {
              if (typeof value !== 'string') {
                return false;
              }
              return (
                !value ||
                new Date(value).valueOf() >= new Date(context.parent.transactionDate).valueOf()
              );
            },
          ),
      }),
    address: yup.string().nullable(),
    unassignedLocation: yup.boolean().nullable(),
    latitude: yup.number().nullable(),
    longitude: yup.number().nullable(),
    countryUniqueId: yup.string().nullable(),
    city: yup.string().nullable(),
    marking: yup
      .object()
      .shape({
        marking: yup
          .string()
          .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            if (
              (typeId === ETransactionTypes.SURPLUS_SALE ||
                typeId === ETransactionTypes.MARKED) &&
              isMiddleTransactionPage
            ) {
              return !!value;
            }
            return true;
          }),
      })
      .nullable(),
    markings: yup.lazy((obj) =>
      yup.object(
        Object.keys(obj || {}).reduce(
          (acc, key) => {
            acc[key] = yup.object().shape({
              marking: yup
                .string()
                .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
                .test('required', t('general.field.validation.required'), (value, params) => {
                  // @ts-expect-error: required by update
                  const typeId = params?.from[2]?.value.typeUniqueId;
                  if (typeId === ETransactionTypes.SURPLUS_SALE && isGroupTransactionPage) {
                    return !!value;
                  }
                  return true;
                })
                .test(
                  'required-min-one',
                  t('transaction.validation.oneMarking.required'),
                  (value, params) => {
                    // @ts-expect-error: required by update
                    const typeId = params?.from[2]?.value.typeUniqueId;
                    if (typeId === ETransactionTypes.MARKED && isGroupTransactionPage) {
                      const notEmptyValues = Object.keys(obj).filter((key: any) =>
                        obj[key]?.marking?.trim(),
                      );
                      return !!notEmptyValues.length;
                    }
                    return true;
                  },
                ),
            });
            return acc;
          },
          {} as Record<string, any>,
        ),
      ),
    ),
    additionalData: yup
      .object({
        yearOfConfiscation: yup
          .string()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value,
          )
          .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
          .nullable()
          .notRequired(),

        yearOfTransfer: yup
          .string()
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            if (typeId === ETransactionTypes.SURPLUS_SALE && isMiddleTransactionPage) {
              return !isEmpty(value);
            }
            return true;
          })
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value,
          )
          .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100')),
        serialNumber: yup
          .string()
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            if (
              typeId === ETransactionTypes.REPURPOSED &&
              !registeredFirearm?.serialNumber &&
              isMiddleTransactionPage
            ) {
              return !isEmpty(value);
            }
            return true;
          }),
      })
      .nullable()
      .notRequired(),
    additionalDataMap: yup
      .array()
      .transform((value, originalValue) => {
        if (originalValue && !Array.isArray(originalValue)) {
          return Object.values(originalValue);
        }
        return value;
      })
      .of(
        yup.object({
          yearOfConfiscation: yearSchema,
          yearOfTransfer: yearSchema.test(
            'required',
            t('general.field.validation.required'),
            (value, params) => {
              // @ts-expect-error: required by update
              const typeId = params?.from[1]?.value.typeUniqueId;
              if (typeId === ETransactionTypes.SURPLUS_SALE && isGroupTransactionPage) {
                return !isEmpty(value);
              }
              return true;
            },
          ),
          serialNumber: yup
            .string()
            .test('required', t('general.field.validation.required'), (value, params) => {
              // @ts-expect-error: required by update
              const typeId = params?.from[1]?.value.typeUniqueId;
              if (typeId === ETransactionTypes.REPURPOSED && isGroupTransactionPage) {
                return !isEmpty(value);
              }
              return true;
            }),
        }),
      )
      .nullable()
      .notRequired(),
  });

  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
    getValues,
    clearErrors,
  } = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver,
  });
  const attachments = useFieldArray({
    control,
    name: 'attachments',
  });

  const [legalEntityIndividualKeeper, setLegalEntityIndividualKeeper] =
    useState<ILegalEntityData | null>();
  const [legalEntityOrganisationKeeper, setLegalEntityOrganisationKeeper] =
    useState<IOrgLegalEntityData | null>();
  const [legalEntityIndividualOwner, setLegalEntityIndividualOwner] =
    useState<ILegalEntityData | null>();
  const [legalEntityOrganisationOwner, setLegalEntityOrganisationOwner] =
    useState<IOrgLegalEntityData | null>();

  const [isLegalEntityLoading, setIsLegalEntityLoading] = useState<boolean>(false);
  const [isErrorLocation, setIsErrorLocation] = useState<boolean>(false);
  const {
    loadDictionaries,
    countryAll,
    getCustomField,
    manufacturer,
    make,
    model,
    caliber,
    firearmAction,
  } = useLegalEntities();
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const { transactionMiddleTypes } = useDictionary();
  const { setAlert } = useAlert();
  const [createTransactionError, setCreateTransactionError] = useState<string>('');
  const { selectedLanguage } = useLang();
  const [possibleTransactionDictionaries, setPossibleTransactionDictionaries] = useState<
    IDictionary[] | null
  >(null);
  const [isCurrentOwnerLoaded, setIsCurrentOwnerLoaded] = useState(false);
  const [isCurrentKeeperLoaded, setIsCurrentKeeperLoaded] = useState(false);

  const [unassignedLocationModalOpened, setUnassignedLocationModalOpened] = useState(false);

  const transactionTypeValue = watch('typeUniqueId');
  const unassignedLocation = watch('unassignedLocation');
  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const address = watch('address');
  const addressId = watch('addressId');

  const isShowTracingDocumentsGeneratedMessage = [
    ETransactionTypes.STOLEN,
    ETransactionTypes.LOST,
  ].includes(transactionTypeValue);
  const isShowTestFiredDocumentsGeneratedMessage = [ETransactionTypes.TEST_FIRED].includes(
    transactionTypeValue,
  );

  const isShowMarking = [
    ETransactionTypes.EXPORTED,
    ETransactionTypes.DEACTIVATED,
    ETransactionTypes.MARKED,
    ETransactionTypes.CONFISCATED,
    ETransactionTypes.SURPLUS_SALE,
    ETransactionTypes.DECLARED_IMPORTED,
  ].includes(transactionTypeValue);

  const isReqiredMarking = [ETransactionTypes.SURPLUS_SALE].includes(transactionTypeValue);

  const withoutLocationSelect = [
    ETransactionTypes.MODIFIED,
    ETransactionTypes.REPAIRED_OR_MAINTAINED,
    ETransactionTypes.EXPORTED,
    ETransactionTypes.PROOFED,
    ETransactionTypes.MARKED,
    ETransactionTypes.DEACTIVATED,
    ETransactionTypes.DESTROYED,
    ETransactionTypes.DEPLETED,
    ETransactionTypes.TEST_FIRED,
  ].includes(transactionTypeValue);

  const isShowCountryOfImport = [ETransactionTypes.DECLARED_IMPORTED].includes(
    transactionTypeValue,
  );
  const isModificationDescription = [ETransactionTypes.MODIFIED].includes(
    transactionTypeValue,
  );
  const isShowCountryOfExport = transactionTypeValue === ETransactionTypes.EXPORTED;
  const canChangeLegalEntity = [...CHANGE_LE_REQUIRED_TRANSACTION_TYPES]
    .filter((item) => !!item)
    .includes(transactionTypeValue);

  const newOwnerIsKeeper = [ETransactionTypes.CONFISCATED].includes(transactionTypeValue);

  const canSetNewKeeperOnly = [
    ETransactionTypes.GIVEN,
    ETransactionTypes.LEASED,
    ETransactionTypes.ASSIGNED_TEMPORARILY,
    ETransactionTypes.ASSIGNED_INDEFINITELY,
    ETransactionTypes.RETURNED,
    ETransactionTypes.SEIZED,
  ].includes(transactionTypeValue);
  const isShowExpirationDate = [ETransactionTypes.TEST_FIRED].includes(transactionTypeValue);
  const isShowExpirationTime = [
    ETransactionTypes.ASSIGNED_TEMPORARILY,
    ETransactionTypes.LEASED,
  ].includes(transactionTypeValue);
  const isMapOnlyForOrganization = [
    ETransactionTypes.RECOVERED,
    ETransactionTypes.SURRENDERED,
    ETransactionTypes.SEIZED,
  ].includes(transactionTypeValue);

  const isShowYearOfTransfer = transactionTypeValue === ETransactionTypes.SURPLUS_SALE;
  const isShowYearOfConfiscation = transactionTypeValue === ETransactionTypes.CONFISCATED;
  const isShowSerialNumber = transactionTypeValue === ETransactionTypes.REPURPOSED;

  const { translations } = useLang();
  const { hasPermissions } = usePermission();

  const selectedNewLegalIfRequired =
    !canChangeLegalEntity ||
    (canChangeLegalEntity && routerState && routerState.legalEntityId);

  useEffect(() => {
    loadInitData();
  }, []);

  useEffect(() => {
    if (firearmGroup && !firearmGroup?.firearms?.length) {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {} as IAlert);
      navigate(
        ERouteLinks.ViewLegalEntityGroup.replace(':id', id).replace(':groupId', groupId),
      );
    }
  }, [firearmGroup]);

  useEffect(() => {
    if (
      registeredFirearm?.groupName &&
      registeredFirearm?.stateUniqueId !== EFirearmStateId.EXPORTED &&
      registeredFirearm?.stateUniqueId !== EFirearmStateId.LOST
    ) {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {} as IAlert);
      navigate(`${ERouteLinks.ViewRegisteredFirearm.replace(':id', id)}?expand=transactions`);
    }
  }, [registeredFirearm]);

  useEffect(() => {
    if (isMiddleTransactionPage && id) {
      getRegisteredFirearm(id).then((res) => {
        setRegisteredFirearm(res);
      });
    } else if (isGroupTransactionPage && groupId) {
      getFirearmGroupDetails(groupId).then((res) => {
        setFirearmGroup(res);
      });
    }
  }, [id]);

  useEffect(() => {
    if (
      possibleTransactionDictionaries &&
      !possibleTransactionDictionaries?.map((d) => d.uniqueId).includes(transactionTypeValue)
    ) {
      setValue('typeUniqueId', '');
    }
  }, [possibleTransactionDictionaries]);

  useEffect(() => {
    if (firearmGroup) {
      const haveFirearmsSameFRT =
        !!firearmGroup?.firearms?.length &&
        firearmGroup?.firearms?.every(
          (f) => f.frtUniqueId === firearmGroup?.firearms[0].frtUniqueId,
        );
      setIsFrtInformationVisible(haveFirearmsSameFRT);
    } else if (registeredFirearm) {
      setIsFrtInformationVisible(true);
    }
  }, [firearmGroup, registeredFirearm]);

  useEffect(() => {
    (async function initFRTData() {
      if (isFrtInformationVisible && firearmGroup?.firearms?.length) {
        await getFRTDetailsData(firearmGroup?.firearms[0].frtUniqueId);
      }
    })();
  }, [isFrtInformationVisible]);

  useEffect(() => {
    if (isTransactionForGroup === '0' && id) {
      getLastFirearmTransactionUniqueId(id).then((result) =>
        setLastTransactionUniqueId(result.transactionUniqueId),
      );
    }
    if (isTransactionForGroup === '1') {
      getGroupLastTransactionUniqueId(groupId).then((result) =>
        setLastTransactionUniqueId(result.transactionUniqueId),
      );
    }
  }, [isTransactionForGroup, id]);

  useEffect(() => {
    if (!!id && isTransactionForGroup === '0' && !!type) {
      getPossibleTransactionType(id, type).then((possibleTransactions) => {
        setPossibleTransactionDictionaries(possibleTransactions || null);
      });
    }
    if (isTransactionForGroup === '1' && !!type) {
      getPossibleTransactionTypeForGroup(groupId, type).then((possibleTransactions) => {
        setPossibleTransactionDictionaries(possibleTransactions || null);
      });
    }
    if (!type) {
      if (firearm && firearm.keeperId) {
        getLegalType(firearm.keeperId).then((legalType) => {
          if (legalType) {
            getPossibleTransactionType(id, legalType.type).then((possibleTransactions) => {
              setPossibleTransactionDictionaries(possibleTransactions || null);
            });
          }
        });
      } else if (isTransactionForGroup === '0') {
        getPossibleTransactionType(id, undefined).then((possibleTransactions) => {
          setPossibleTransactionDictionaries(possibleTransactions || null);
        });
      }
    }
  }, [id, groupId, type, selectedLanguage]);

  useEffect(() => {
    if (transactionTypeValue && !canChangeLegalEntity) {
      setNewFirearmLocationOwner(
        legalEntityIndividualKeeper?.uniqueId ||
          legalEntityOrganisationKeeper?.uniqueId ||
          legalEntityIndividualOwner?.uniqueId ||
          legalEntityOrganisationOwner?.uniqueId ||
          null,
      );
      setNewFirearmLocationOwnerInserted(true);
    } else if (
      transactionTypeValue &&
      canChangeLegalEntity &&
      !newFirearmLocationOwnerInserted &&
      routerState
    ) {
      setNewFirearmLocationOwnerInserted(true);
      setNewFirearmLocationOwner(routerState.legalEntityId);
    }
  }, [
    transactionTypeValue,
    legalEntityIndividualKeeper,
    legalEntityOrganisationKeeper,
    legalEntityIndividualOwner,
    legalEntityOrganisationOwner,
  ]);

  useEffect(() => {
    if (isTransactionForGroup === '0') {
      setIsLegalEntityLoading(true);
      getFirearmLastTransaction(id, true).then((result) => {
        if (result.ownerId) {
          getLegalType(result.ownerId).then((legalType) => {
            if (legalType!.type === LETypes.organization) {
              getOrgLegalEntity(result.ownerId).then((owner) => {
                setLegalEntityOrganisationOwner(owner);
                setIsCurrentOwnerLoaded(true);
              });
            } else {
              getLegalEntity(result.ownerId).then((owner) => {
                setLegalEntityIndividualOwner(owner);
                setIsCurrentOwnerLoaded(true);
              });
            }
          });
        } else {
          setIsCurrentOwnerLoaded(true);
        }
        if (result.keeperId) {
          getLegalType(result.keeperId).then((legalType) => {
            if (legalType) {
              setType(legalType.type);
              if (legalType!.type === LETypes.organization) {
                getOrgLegalEntity(result.keeperId).then((keeper) => {
                  setLegalEntityOrganisationKeeper(keeper);
                  setIsCurrentKeeperLoaded(true);
                });
              } else {
                getLegalEntity(result.keeperId).then((keeper) => {
                  setLegalEntityIndividualKeeper(keeper);
                  setIsCurrentKeeperLoaded(true);
                });
              }
            }
          });
        } else {
          setIsCurrentKeeperLoaded(true);
        }
        setIsLegalEntityLoading(false);
      });
    } else {
      setIsLegalEntityLoading(true);
      getGroupLastTransaction(groupId).then((result) => {
        if (result.ownerId) {
          getLegalType(result.ownerId).then((legalType) => {
            if (legalType) {
              if (legalType!.type === LETypes.organization) {
                getOrgLegalEntity(result.ownerId).then((owner) => {
                  setLegalEntityOrganisationOwner(owner);
                  setIsCurrentOwnerLoaded(true);
                });
              } else {
                getLegalEntity(result.ownerId).then((owner) => {
                  setLegalEntityIndividualOwner(owner);
                  setIsCurrentOwnerLoaded(true);
                });
              }
              if (!result.keeperId) {
                setType(legalType.type);
              }
            }
          });
        } else {
          setIsCurrentOwnerLoaded(true);
        }
        if (result.keeperId) {
          getLegalType(result.keeperId).then((legalType) => {
            if (legalType) {
              setType(legalType.type);
              if (legalType!.type === LETypes.organization) {
                getOrgLegalEntity(result.keeperId).then((keeper) => {
                  setLegalEntityOrganisationKeeper(keeper);
                  setIsCurrentKeeperLoaded(true);
                });
              } else {
                getLegalEntity(result.keeperId).then((keeper) => {
                  setLegalEntityIndividualKeeper(keeper);
                  setIsCurrentKeeperLoaded(true);
                });
              }
            }
          });
        } else {
          setIsCurrentKeeperLoaded(true);
        }
      });
      setIsLegalEntityLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async function initData() {
      await getCustomField(ECustomFieldObjectType.FIREARM);
      await loadDictionaries([
        EDictionaryTypes.CountryAll,
        EDictionaryTypes.TransactionType,
        EDictionaryTypes.Manufacturer,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.Caliber,
        EDictionaryTypes.FirearmAction,
      ]);
    })();
  }, [selectedLanguage]);

  const filteredTransactionMiddleTypes = useRef<IDictionary[] | null>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<(string | IBreadcrumbsLink)[] | []>([]);

  useEffect(() => {
    if (translations?.length && type?.length) {
      setBreadcrumbs(
        isTransactionForGroup === '1'
          ? [
              { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
              {
                label:
                  type === LETypes.individual
                    ? t('legalEntities.individual.accordion.label')
                    : t('legalEntities.organization.accordion.label'),
                route: `${ERouteLinks.LegalEntities}?expand=${
                  type === LETypes.individual ? 'individual' : 'organisation'
                }`,
              },
              {
                label: t('legalEntityDetails.legalEntityDetails.text'),
                route: `${ERouteLinks.ViewLegalEntity.replace(':id', id as string)}?type=${type === LETypes.individual ? 'individual' : 'organisation'}&expand=groups`,
              },
              {
                label: t('groupDetails.header'),
                route: `${ERouteLinks.ViewLegalEntityGroup.replace(
                  ':id',
                  id as string,
                ).replace(
                  ':groupId',
                  groupId as string,
                )}?type=${type === LETypes.individual ? 'individual' : 'organisation'}&expand=groups`,
              },
              t('addTransaction.addTransaction.breadcrumb'),
            ]
          : [
              { label: t('addTransaction.firearm.breadcrumb'), route: ERouteLinks.Firearms },
              {
                label: t('addTransaction.firearmDetails.breadcrumb'),
                route: ERouteLinks.ViewRegisteredFirearm.replace(':id', id),
              },
              t('addTransaction.addTransaction.breadcrumb'),
            ],
      );
    }
  }, [translations, type]);

  useEffect(() => {
    if (transactionMiddleTypes) {
      if (type === LETypes.individual) {
        filteredTransactionMiddleTypes.current = transactionMiddleTypes?.filter(
          ({ uniqueId: id1 }) =>
            !TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS.some((id2) => id2 === id1),
        );
      } else if (type === LETypes.organization) {
        filteredTransactionMiddleTypes.current = transactionMiddleTypes;
      }
    }
  }, [type, transactionMiddleTypes]);

  useEffect(() => () => window.history.replaceState({}, document.title), []);

  const prepareFrtTitleDetails = (
    makeUniqueId: string,
    modelUniqueId: string,
    actionUniqueId: string,
    manufacturerUniqueId: string,
    caliberUniqueId: string,
  ) => {
    const manufacturerName = manufacturerUniqueId
      ? `${findInDictionary(manufacturerUniqueId, manufacturer)}, `
      : '';
    const calibreName = caliberUniqueId
      ? `, ${findInDictionary(caliberUniqueId, caliber)} `
      : '';

    setFrtTitleDetails(
      `${findInDictionary(makeUniqueId, make)}, ${findInDictionary(modelUniqueId, model)}, ${manufacturerName} ${findInDictionary(actionUniqueId, firearmAction)}${calibreName}`,
    );
  };

  useEffect(() => {
    if (routerState?.firearm) {
      const {
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
        uniqueId,
      } = routerState.firearm;

      prepareFrtTitleDetails(
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
      );
      setFrtUrlDetails(
        uniqueId
          ? ERouteLinks.ViewRegisteredFirearm.replace(':id', uniqueId as string)
          : undefined,
      );
    } else if (firearmGroup?.firearms.length) {
      const {
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
      } = firearmGroup.firearms[0];
      prepareFrtTitleDetails(
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
      );
      setFrtUrlDetails(
        `${ERouteLinks.ViewLegalEntityGroup.replace(':id', id as string).replace(
          ':groupId',
          groupId as string,
        )}?type=${type === LETypes.individual ? 'individual' : 'organisation'}&expand=groups`,
      );
    } else if (registeredFirearm) {
      const {
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
        uniqueId,
      } = registeredFirearm;
      prepareFrtTitleDetails(
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
      );
      setFrtUrlDetails(
        uniqueId
          ? ERouteLinks.ViewRegisteredFirearm.replace(':id', uniqueId as string)
          : undefined,
      );
    }
  }, [
    routerState,
    firearmGroup,
    registeredFirearm,
    selectedLanguage,
    make,
    model,
    manufacturer,
    caliber,
    firearmAction,
  ]);

  const clearUserInput = () => {
    setValue('countryOfImportUniqueId', '');
    setValue('countryOfExportUniqueId', '');
    setValue('expirationDate', '');
    setValue('expirationTime', '');
    setValue('importer', '');
    setValue('legalEntityId', '');
    setValue('legalEntityFullName', '');
    setValue('address', '');
    setValue('countryUniqueId', null);
    setValue('city', null);
    setValue('latitude', null);
    setValue('longitude', null);
    setValue('addressId', null);
    setValue('unassignedLocation', false);

    setNewFirearmLocationOwner(null);
  };

  const onCancel = () => {
    if (isMiddleTransactionPage) {
      navigate(`${ERouteLinks.ViewRegisteredFirearm.replace(':id', id)}?expand=transactions`);
    } else if (isGroupTransactionPage) {
      navigate(
        `${ERouteLinks.ViewLegalEntityGroup.replace(':id', id as string).replace(
          ':groupId',
          groupId as string,
        )}?type=${type === LETypes.individual ? 'individual' : 'organisation'}&expand=groups`,
      );
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', () => {
      if (window.location.href.includes(ERouteLinks.SelectLegalEntity)) {
        onCancel();
      }
    });
  }, []);

  const onSubmit = () => {
    let ownerId: any = null;
    let keeperId: any = null;
    setCreateTransactionError('');
    handleSubmit(async (data) => {
      setIsErrorLocation(false);
      const date = formatDateTimeToIso(data.transactionDate);

      const fullDate = date;
      if (canSetNewKeeperOnly) {
        if (type === LETypes.individual || type === LETypes.organization) {
          keeperId = routerState?.legalEntityId;
          ownerId =
            legalEntityIndividualOwner?.uniqueId || legalEntityOrganisationOwner?.uniqueId;
        } else {
          keeperId = routerState.legalEntityId;
          ownerId =
            legalEntityIndividualOwner !== undefined && legalEntityIndividualOwner !== null
              ? legalEntityIndividualOwner.uniqueId
              : legalEntityOrganisationOwner?.uniqueId;
        }
      } else if ([ETransactionTypes.TEST_FIRED].includes(transactionTypeValue)) {
        keeperId = legalEntityIndividualKeeper
          ? legalEntityIndividualKeeper.uniqueId
          : legalEntityOrganisationKeeper?.uniqueId;
        ownerId = legalEntityIndividualOwner
          ? legalEntityIndividualOwner.uniqueId
          : legalEntityOrganisationOwner?.uniqueId;
      } else if (
        [ETransactionTypes.STOLEN, ETransactionTypes.LOST].includes(transactionTypeValue)
      ) {
        keeperId = null;
        ownerId = legalEntityIndividualOwner
          ? legalEntityIndividualOwner.uniqueId
          : legalEntityOrganisationOwner?.uniqueId;
      } else if ([ETransactionTypes.CONFISCATED].includes(transactionTypeValue)) {
        keeperId = legalEntityIndividualKeeper
          ? legalEntityIndividualKeeper.uniqueId
          : legalEntityOrganisationKeeper?.uniqueId;
        ownerId = legalEntityIndividualKeeper
          ? legalEntityIndividualKeeper.uniqueId
          : legalEntityOrganisationKeeper?.uniqueId;
      } else if ([ETransactionTypes.REPURPOSED].includes(transactionTypeValue)) {
        keeperId = legalEntityIndividualKeeper
          ? legalEntityIndividualKeeper.uniqueId
          : legalEntityOrganisationKeeper?.uniqueId;
        ownerId = legalEntityIndividualOwner
          ? legalEntityIndividualOwner.uniqueId
          : legalEntityOrganisationOwner?.uniqueId;
      } else if (
        [ETransactionTypes.EXPORTED, ETransactionTypes.DESTROYED].includes(
          transactionTypeValue,
        )
      ) {
        keeperId = null;
        ownerId = null;
      } else if ([ETransactionTypes.SPOT_CHECK].includes(transactionTypeValue)) {
        if (legalEntityIndividualKeeper) {
          keeperId = legalEntityIndividualKeeper.uniqueId;
        } else if (legalEntityOrganisationKeeper) {
          keeperId = legalEntityOrganisationKeeper.uniqueId;
        }
        if (legalEntityIndividualOwner) {
          ownerId = legalEntityIndividualOwner.uniqueId;
        } else if (legalEntityOrganisationOwner) {
          ownerId = legalEntityOrganisationOwner.uniqueId;
        }
      } else if ([ETransactionTypes.REPAIRED_OR_MAINTAINED].includes(transactionTypeValue)) {
        keeperId = legalEntityIndividualKeeper
          ? legalEntityIndividualKeeper.uniqueId
          : legalEntityOrganisationKeeper?.uniqueId;
        ownerId = legalEntityIndividualOwner
          ? legalEntityIndividualOwner.uniqueId
          : legalEntityOrganisationOwner?.uniqueId;
      } else if ([ETransactionTypes.DEACTIVATED].includes(transactionTypeValue)) {
        if (legalEntityIndividualKeeper) {
          keeperId = legalEntityIndividualKeeper.uniqueId;
        } else if (legalEntityOrganisationKeeper) {
          keeperId = legalEntityOrganisationKeeper.uniqueId;
        }
        if (legalEntityIndividualOwner) {
          ownerId = legalEntityIndividualOwner.uniqueId;
        } else if (legalEntityOrganisationOwner) {
          ownerId = legalEntityOrganisationOwner.uniqueId;
        }
      } else if ([ETransactionTypes.PROOFED].includes(transactionTypeValue)) {
        if (legalEntityIndividualKeeper) {
          keeperId = legalEntityIndividualKeeper.uniqueId;
        } else if (legalEntityOrganisationKeeper) {
          keeperId = legalEntityOrganisationKeeper.uniqueId;
        }
        if (legalEntityIndividualOwner) {
          ownerId = legalEntityIndividualOwner.uniqueId;
        } else if (legalEntityOrganisationOwner) {
          ownerId = legalEntityOrganisationOwner.uniqueId;
        }
      } else if (
        [ETransactionTypes.MODIFIED, ETransactionTypes.MARKED].includes(transactionTypeValue)
      ) {
        if (legalEntityIndividualKeeper) {
          keeperId = legalEntityIndividualKeeper.uniqueId;
        } else if (legalEntityOrganisationKeeper) {
          keeperId = legalEntityOrganisationKeeper.uniqueId;
        }
        if (legalEntityIndividualOwner) {
          ownerId = legalEntityIndividualOwner.uniqueId;
        } else if (legalEntityOrganisationOwner) {
          ownerId = legalEntityOrganisationOwner.uniqueId;
        }
      } else if (routerState?.type || routerState?.legalEntityId) {
        keeperId = routerState?.legalEntityId;
        ownerId = routerState?.legalEntityId;
      } else if (type === LETypes.individual) {
        keeperId = legalEntityIndividualKeeper
          ? legalEntityIndividualKeeper.uniqueId
          : legalEntityOrganisationKeeper?.uniqueId;
        ownerId = legalEntityIndividualOwner
          ? legalEntityIndividualOwner.uniqueId
          : legalEntityOrganisationOwner?.uniqueId;
      } else if (type === LETypes.organization) {
        keeperId = legalEntityOrganisationKeeper?.uniqueId;
        ownerId = legalEntityOrganisationOwner?.uniqueId;
      }

      if (
        !withoutLocationSelect &&
        !(data.latitude && data.longitude) &&
        !data.unassignedLocation &&
        !addressId
      ) {
        setSubmitBlocked(false);
        return setIsErrorLocation(true);
      }
      const body = {
        request: {
          typeUniqueId: data.typeUniqueId,
          keeperId,
          ownerId,
          date: fullDate,
          ...(isTransactionForGroup === '1' ? { groupUniqueId: groupId } : {}),
          ...(isTransactionForGroup === '0' ? { firearmUniqueId: id } : {}),
          ...(isShowCountryOfImport
            ? {
                countryOfImportUniqueId: data.countryOfImportUniqueId,
                importer: data.importer,
              }
            : {}),
          ...(isShowCountryOfExport
            ? {
                countryOfExportUniqueId: data.countryOfExportUniqueId,
              }
            : {}),
          ...(!withoutLocationSelect &&
          (routerState?.coords || (data.latitude && data.longitude))
            ? {
                latitude: data.latitude,
                longitude: data.longitude,
                address: data.address,
                city: data.city,
                countryUniqueId: data.countryUniqueId,
                unassignedLocation: data.unassignedLocation,
              }
            : {}),
          ...(!withoutLocationSelect &&
          !routerState?.coords &&
          (!data.latitude || !data.longitude)
            ? {
                unassignedLocation: data.unassignedLocation,
              }
            : {}),
          ...(isModificationDescription ? { description: data.description } : {}),
          ...(isShowExpirationDate
            ? {
                expirationDate: data.expirationDate
                  ? formatDateTimeToIso(data.expirationDate)
                  : null,
              }
            : {}),
          ...(isShowExpirationTime
            ? {
                expirationDate: formatDateTimeToIso(data.expirationTime),
              }
            : {}),
          ...(isShowYearOfTransfer && isMiddleTransactionPage
            ? {
                additionalData: {
                  yearOfTransfer: data.additionalData.yearOfTransfer,
                },
              }
            : {}),
          ...(isShowSerialNumber && isMiddleTransactionPage && !registeredFirearm?.serialNumber
            ? {
                additionalData: {
                  serialNumber: data.additionalData.serialNumber,
                },
              }
            : {}),
          ...(isShowYearOfConfiscation && isMiddleTransactionPage
            ? {
                additionalData: {
                  yearOfConfiscation: data.additionalData.yearOfConfiscation,
                },
              }
            : {}),

          ...(isShowYearOfTransfer && isGroupTransactionPage
            ? {
                additionalDataMap: firearmGroup?.firearms.reduce<
                  Record<string, { yearOfTransfer: string }>
                >((acc, frm, index) => {
                  const year = data.additionalDataMap[index]?.yearOfTransfer;
                  if (year && year.toString().trim()) {
                    acc[frm.uniqueId] = {
                      yearOfTransfer: year,
                    };
                  }
                  return acc;
                }, {}),
              }
            : {}),
          ...(isShowSerialNumber && isGroupTransactionPage
            ? {
                additionalDataMap: firearmGroup?.firearms.reduce<
                  Record<string, { serialNumber: string }>
                >((acc, frm, index) => {
                  if (!data.additionalDataMap) return acc;

                  const serialNumber = data.additionalDataMap[index]?.serialNumber;
                  if (serialNumber && serialNumber.toString().trim()) {
                    acc[frm.uniqueId] = {
                      serialNumber: serialNumber,
                    };
                  }
                  return acc;
                }, {}),
              }
            : {}),
          ...(isShowYearOfConfiscation && isGroupTransactionPage
            ? {
                additionalDataMap: firearmGroup?.firearms.reduce<
                  Record<string, { yearOfConfiscation: string }>
                >((acc, frm, index) => {
                  const year = data.additionalDataMap[index]?.yearOfConfiscation;
                  if (year && year.toString().trim()) {
                    acc[frm.uniqueId] = {
                      yearOfConfiscation: year,
                    };
                  }
                  return acc;
                }, {}),
              }
            : {}),
          ...(isShowMarking && isMiddleTransactionPage && data.marking.marking
            ? {
                marking: {
                  firearmUniqueId: id,
                  marking: data.marking.marking,
                  markingType: getMarkingType(data.typeUniqueId, data.marking.markingType),
                },
              }
            : {}),
          ...(isShowMarking && isGroupTransactionPage
            ? {
                markings: firearmGroup?.firearms
                  .map((frm, index) => {
                    if (
                      data.markings[index].marking &&
                      data.markings[index].marking.toString().trim()
                    )
                      return {
                        firearmUniqueId: frm.uniqueId,
                        marking: data.markings[index].marking,
                        markingType: getMarkingType(
                          data.typeUniqueId,
                          data.markings[index].markingType,
                        ),
                      };
                  })
                  .filter((item) => item),
              }
            : {}),
          lastTransactionUniqueId,
        },
        ...(data.attachments?.filter((attachment: any) => attachment.file !== null)?.length
          ? { attachments: data.attachments }
          : {}),
      };
      try {
        setSubmitBlocked(true);
        if (isTransactionForGroup === '1') {
          const transaction = await createMiddleTransactionForGroup(body);
          if (transaction) {
            if (isShowTracingDocumentsGeneratedMessage) {
              if (eTraceEnabled || iArmsEnabled) {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.tracingDocumentsGenerated.text'),
                });
              } else {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.text'),
                });
              }
            } else if (isShowTestFiredDocumentsGeneratedMessage) {
              if (eTraceEnabled) {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.testFireDocumentsGenerated.text'),
                });
              } else {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.text'),
                });
              }
            } else {
              setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                text: t('addTransaction.transactionAdded.text'),
              });
            }
            navigate(pathname, {
              state: {
                ...routerState,
                typeUniqueId: undefined,
                transactionDate: undefined,
                firearmsQuantity: undefined,
                serialNumber: undefined,
                appliedMarking: undefined,
                manufacturedAt: undefined,
                countryOfImportUniqueId: undefined,
                importer: undefined,
                countryOfExportUniqueId: undefined,
                expirationDate: undefined,
                expirationTime: undefined,
                attachments: undefined,
                legalEntityFullName: undefined,
                legalEntityId: undefined,
                address: undefined,
                currentKeeperUniqueId: undefined,
                currentOwnerUniqueId: undefined,
                type: undefined,
                breadcrumbs: undefined,
              },
              replace: true,
            });
            navigate(
              `${ERouteLinks.ViewTransaction.replace(
                ':id',
                transaction.uniqueId,
              )}?expand = firearmDetails`,
            );
          }
        } else if (isTransactionForGroup === '0') {
          const transaction = await createMiddleTransaction(body);
          if (transaction) {
            if (isShowTracingDocumentsGeneratedMessage) {
              if (eTraceEnabled || iArmsEnabled) {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.tracingDocumentsGenerated.text'),
                });
              } else {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.text'),
                });
              }
            } else if (isShowTestFiredDocumentsGeneratedMessage) {
              if (eTraceEnabled) {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.testFireDocumentsGenerated.text'),
                });
              } else {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                  text: t('addTransaction.transactionAdded.text'),
                });
              }
            } else {
              setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
                text: t('addTransaction.transactionAdded.text'),
              });
            }
            clearErrors();
            navigate(pathname, {
              state: {
                ...routerState,
                typeUniqueId: undefined,
                transactionDate: undefined,
                firearmsQuantity: undefined,
                serialNumber: undefined,
                appliedMarking: undefined,
                manufacturedAt: undefined,
                countryOfImportUniqueId: undefined,
                importer: undefined,
                countryOfExportUniqueId: undefined,
                expirationDate: undefined,
                expirationTime: undefined,
                attachments: undefined,
                legalEntityFullName: undefined,
                legalEntityId: undefined,
                address: undefined,
                currentKeeperUniqueId: undefined,
                currentOwnerUniqueId: undefined,
                type: undefined,
                breadcrumbs: undefined,
              },
              replace: true,
            });
            navigate(
              `${ERouteLinks.ViewTransaction.replace(
                ':id',
                transaction.uniqueId,
              )}?expand = firearmDetails`,
            );
          }
        }
      } catch (e: any) {
        setCreateTransactionError(e?.response?.data?.message);
        setSubmitBlocked(false);
      }
      return true;
    })();
  };

  useEffect(() => {
    (async function () {
      scrollToError(errors);
    })();
  }, [errors]);

  const validateFile = (file: File, index: number) => {
    const fileType: any = file.type;
    if (file) {
      if (file.size === 0) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.emptyFile.text'),
        });
        return false;
      }
      const fileSizeInMB = file.size / 1024 / 1024;
      if (file.size > 0 && fileSizeInMB > MAX_FILE_SIZE_IN_MB) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileSizeValidation.text'),
        });
        return false;
      }
      if (
        ![
          EMIMETypes.JPG,
          EMIMETypes.JPEG,
          EMIMETypes.PNG,
          EMIMETypes.PDF,
          EMIMETypes.XLSX,
        ].includes(fileType)
      ) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileTypeValidation.text'),
        });
        return false;
      }
    }
    clearErrors('attachments');
    return true;
  };

  const handleUploadFile = (file: File, index: number) => {
    if (validateFile(file, index)) {
      attachments.update(index, {
        file,
      });
    }
  };

  const renderRemoveAttachmentButton = (index: number) => (
    <Grid
      size={{ xs: 1 }}
      sx={!isDesktop ? { display: 'flex', justifyContent: 'flex-end' } : {}}
    >
      <Button
        variant={EButtonVariants.outlined}
        icon={EIconTypes.delete}
        onClick={() => attachments.remove(index)}
        sizePX={EButtonSizesPX.px32}
        sx={{ ...(isDesktop ? { marginLeft: '20px' } : {}), marginTop: '28px' }}
      />
    </Grid>
  );

  const getCustomFieldTranslation = (customFieldTranslation?: ITranslation[]) => {
    if (selectedLanguage && customFieldTranslation) {
      return customFieldTranslation.filter(
        (trans) => trans.languageUniqueId === selectedLanguage?.uniqueId,
      )[0].name;
    }
    return '';
  };

  const renderCustomFieldInputs = (frm: IFirearm) =>
    frm?.customFields?.map(
      (customField) =>
        customField.value && (
          <Grid size={{ xs: 12, md: 6, lg: 3 }} key={customField.customFieldUniqueId}>
            <FormInput
              label={getCustomFieldTranslation(customField?.translations)}
              name={customField.customFieldName}
              control={control}
              setValue={setValue}
              errors={errors}
              defaultValue={
                customField.customFieldType === ECustomFieldType.DATE
                  ? formatDate(customField.value, shortDateFormat)
                  : customField.value
              }
              disabled
            />
          </Grid>
        ),
    );

  const handleLimitToParam = () => {
    if (isMapOnlyForOrganization) {
      return `?limitTo=${LETypes.organization}`;
    }
    if (transactionTypeValue === ETransactionTypes.SURPLUS_SALE) {
      return `?prefixGroup=${EDictionaryPrefixTypesUniqueId.civilianPrefix}&organisationCategory=${EOrganisationCategory.civilian}`;
    }
    return '';
  };

  const attachmentNames = watch('attachments');

  useEffect(() => {
    attachments.fields.forEach((attachment: any, index) => {
      if (attachment?.file?.name && !attachmentNames[index]?.attachmentName) {
        setValue(
          `attachments.${index}.attachmentName`,
          removeFileExtension(attachment.file.name),
        );
      }
    });
  }, [attachments.fields, attachmentNames]);

  const getMarkingLabel = (transactionType = transactionTypeValue) => {
    switch (transactionType) {
      case ETransactionTypes.EXPORTED:
        return t('addTransaction.document.section.exportMarking.label');
      case ETransactionTypes.DEACTIVATED:
        return t('addTransaction.document.section.deactivationMarking.label');
      case ETransactionTypes.MARKED:
        return t('addTransaction.document.section.markedMarking.label');
      case ETransactionTypes.DECLARED_IMPORTED:
        return t('addFirearm.firearmList.section.importMarking.label');
      case ETransactionTypes.DECLARED_MANUFACTURED:
      case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
      case ETransactionTypes.SEIZED:
      case ETransactionTypes.RECOVERED:
      case ETransactionTypes.SURRENDERED:
        return t('addFirearm.firearmList.section.appliedMarking.label');
      case ETransactionTypes.CONFISCATED:
        return t('addTransaction.document.section.confiscationMarking.label');
      case ETransactionTypes.SURPLUS_SALE:
        return t('addTransaction.document.section.surplusSaleMarking.label');
      default:
        return t('addTransaction.document.section.marking.label');
    }
  };

  const canSelectFromMap = typesWithoutStrictAddressLocation.includes(
    transactionTypeValue as ETransactionTypes,
  );

  return (
    <>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">
          {t('addTransaction.addTransaction.header')}
        </StyledPageTitle>
      </Box>
      <div ref={elRef}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 3 }}>
            <FormSearchSelect
              options={mapDictionaryToOptionProp(
                filterDisabled(possibleTransactionDictionaries, legalEntityIndividualKeeper),
              )}
              name="typeUniqueId"
              label={t('addFirearm.location.section.transactionType')}
              onSelect={clearUserInput}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <FormDateTimePicker
              name="transactionDate"
              label={t('addFirearm.location.section.transactionDate')}
              control={control}
              errors={errors}
              currentHour={true}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('addTransaction.currentOwnerAndKeeper.section.name')}
              alwaysExpanded
            >
              <Box sx={{ mb: 5 }}>
                <div>
                  {isLegalEntityLoading ? (
                    <Loader isLoading={isLegalEntityLoading} />
                  ) : (
                    <StyledDetailsWrapper container spacing={3}>
                      {legalEntityIndividualOwner || legalEntityOrganisationOwner ? (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addTransaction.currentOwnerAndKeeper.section.owner.label')}
                          </Typography>
                          {legalEntityIndividualOwner && (
                            <StyledLegalLink
                              legalId={legalEntityIndividualOwner.uniqueId}
                              legalType={ELegalEntityTypes.individual}
                              labelText={`${legalEntityIndividualOwner?.firstName} ${legalEntityIndividualOwner?.lastName} `}
                              legalVisibilityUniqueId={
                                legalEntityIndividualOwner.visibilityUniqueId
                              }
                            />
                          )}
                          {legalEntityOrganisationOwner && (
                            <StyledLegalLink
                              legalId={legalEntityOrganisationOwner.uniqueId}
                              legalType={ELegalEntityTypes.organisation}
                              labelText={legalEntityOrganisationOwner?.organizationName}
                              legalVisibilityUniqueId={
                                legalEntityOrganisationOwner.visibilityUniqueId
                              }
                            />
                          )}
                        </Grid>
                      ) : (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addTransaction.currentOwnerAndKeeper.section.owner.label')}
                          </Typography>
                          <Typography variant="body2">-</Typography>
                        </Grid>
                      )}
                      {legalEntityOrganisationKeeper || legalEntityIndividualKeeper ? (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addTransaction.currentOwnerAndKeeper.section.keeper.label')}
                          </Typography>
                          {legalEntityIndividualKeeper && (
                            <StyledLegalLink
                              legalId={legalEntityIndividualKeeper.uniqueId}
                              legalType={ELegalEntityTypes.individual}
                              labelText={`${legalEntityIndividualKeeper?.firstName} ${legalEntityIndividualKeeper?.lastName} `}
                              legalVisibilityUniqueId={
                                legalEntityIndividualKeeper.visibilityUniqueId
                              }
                            />
                          )}
                          {legalEntityOrganisationKeeper && (
                            <StyledLegalLink
                              legalId={legalEntityOrganisationKeeper.uniqueId}
                              legalType={ELegalEntityTypes.organisation}
                              labelText={legalEntityOrganisationKeeper?.organizationName}
                              legalVisibilityUniqueId={
                                legalEntityOrganisationKeeper.visibilityUniqueId
                              }
                            />
                          )}
                        </Grid>
                      ) : (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addTransaction.currentOwnerAndKeeper.section.keeper.label')}
                          </Typography>
                          <Typography variant="body2">-</Typography>
                        </Grid>
                      )}
                    </StyledDetailsWrapper>
                  )}
                </div>
              </Box>
            </Accordion>
          </Grid>
          {newOwnerIsKeeper &&
            (legalEntityOrganisationKeeper || legalEntityIndividualKeeper) && (
              <Grid size={{ xs: 12 }}>
                <Accordion
                  title={t('addTransaction.newOwnerAndKeeper.section.name')}
                  alwaysExpanded
                >
                  <Box sx={{ mb: 5 }}>
                    <div>
                      <StyledDetailsWrapper container spacing={3}>
                        {[
                          'addTransaction.currentOwnerAndKeeper.section.owner.label',
                          'addTransaction.currentOwnerAndKeeper.section.keeper.label',
                        ].map((label: string) => (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t(label)}
                            </Typography>
                            {legalEntityIndividualKeeper && (
                              <StyledLegalLink
                                legalId={legalEntityIndividualKeeper.uniqueId}
                                legalType={ELegalEntityTypes.individual}
                                labelText={`${legalEntityIndividualKeeper?.firstName} ${legalEntityIndividualKeeper?.lastName} `}
                                legalVisibilityUniqueId={
                                  legalEntityIndividualKeeper.visibilityUniqueId
                                }
                              />
                            )}
                            {legalEntityOrganisationKeeper && (
                              <StyledLegalLink
                                legalId={legalEntityOrganisationKeeper.uniqueId}
                                legalType={ELegalEntityTypes.organisation}
                                labelText={legalEntityOrganisationKeeper?.organizationName}
                                legalVisibilityUniqueId={
                                  legalEntityOrganisationKeeper.visibilityUniqueId
                                }
                              />
                            )}
                          </Grid>
                        ))}
                      </StyledDetailsWrapper>
                    </div>
                  </Box>
                </Accordion>
              </Grid>
            )}
          {canChangeLegalEntity && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                title={
                  [
                    ETransactionTypes.ASSIGNED_INDEFINITELY,
                    ETransactionTypes.ASSIGNED_TEMPORARILY,
                    ETransactionTypes.GIVEN,
                    ETransactionTypes.RETURNED,
                    ETransactionTypes.LEASED,
                    ETransactionTypes.SEIZED,
                  ].includes(transactionTypeValue)
                    ? t('addTransaction.newKeeper.section.name')
                    : t('addTransaction.newOwnerAndKeeper.section.name')
                }
                alwaysExpanded
              >
                <Grid container size={{ xs: 12 }} rowSpacing={2} sx={{ mb: 5 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormInput
                      name="legalEntityFullName"
                      label={`${t(
                        'addTransaction.newOwnerAndKeeper.section.legalEntity.label',
                      )}* `}
                      control={control}
                      errors={errors}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, lg: 3 }}
                    sx={{ marginTop: { lg: '28px' }, ml: { lg: 2 } }}
                  >
                    <Button
                      id="addTransaction.newOwnerAndKeeper.section.searchForLegalEntity.button"
                      fullWidth
                      label={t(
                        'addTransaction.newOwnerAndKeeper.section.searchForLegalEntity.button',
                      )}
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                      onClick={() => {
                        navigate(`${ERouteLinks.SelectLegalEntity}${handleLimitToParam()} `, {
                          state: {
                            context: 'transactions',
                            ...routerState,
                            ...getValues(),
                            address: null,
                            latitude: null,
                            longitude: null,
                            addressId: null,
                            countryUniqueId: null,
                            unassignedLocation: false,
                            city: false,
                            coords: undefined,
                            id,
                            groupId,
                            currentKeeperUniqueId:
                              legalEntityIndividualKeeper?.uniqueId ||
                              legalEntityOrganisationKeeper?.uniqueId,
                            currentOwnerUniqueId:
                              transactionTypeValue === ETransactionTypes.SOLD
                                ? legalEntityOrganisationOwner?.uniqueId ||
                                  legalEntityIndividualOwner?.uniqueId
                                : undefined,
                            type,
                            breadcrumbs: [
                              ...breadcrumbs,
                              t('transactions.search.selectLegal.label'),
                            ],
                            returnLink:
                              isTransactionForGroup === '1'
                                ? `${ERouteLinks.LegalEntityAddTransactionForGroup.replace(
                                    ':id',
                                    id,
                                  ).replace(':groupId', groupId)}?type=${type}&expand=groups`
                                : `${ERouteLinks.MiddleTransaction.replace(':id', id)} `,
                          },
                        });
                      }}
                    />
                  </Grid>
                </Grid>
              </Accordion>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addTransaction.firearmDetails.section.name')} alwaysExpanded>
              {isFrtInformationVisible && (
                <Grid
                  container
                  sx={{
                    border: `1px solid ${colors.primary25} `,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    mb: 4,
                  }}
                >
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      backgroundColor: colors.primary100,
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                      gap: 1,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Grid sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {frtTitleDetails}
                      </Typography>
                    </Grid>
                    {frtUrlDetails && (
                      <Grid sx={{ width: isMobile ? '100%' : 'auto', flexShrink: 0 }}>
                        <Link
                          href={frtUrlDetails}
                          underline="none"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'block', width: '100%' }}
                        >
                          <Button
                            label={t('general.details.text')}
                            variant={EButtonVariants.outlined}
                            size={EButtonSizes.small}
                            id="addTransaction_firearmDetails_details-button"
                            fullWidth={isMobile}
                          />
                        </Link>
                      </Grid>
                    )}
                  </Grid>
                  <Grid
                    container
                    size={{ xs: 12 }}
                    rowSpacing={2}
                    sx={{ alignItems: 'center', p: 2 }}
                  >
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(
                            'addTransaction.firearmDetails.section.firearmsReferenceNo.label',
                          )}
                        </Typography>
                        <Typography variant="body2">
                          {FRTDetails?.frtId || registeredFirearm?.frtId}
                        </Typography>
                      </Stack>
                    </Grid>
                    {hasPermissions([EPermission.VIEW_FIREARMS]) && (
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <Link
                          href={ERouteLinks.FRTDetails.replace(
                            ':frtId',
                            firearmGroup
                              ? firearmGroup?.firearms[0]?.frtUniqueId
                              : registeredFirearm?.frtUniqueId || '',
                          )}
                          underline="none"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            fullWidth
                            label={t(
                              'addTransaction.firearmDetails.section.showFRTInformation.button',
                            )}
                            variant={EButtonVariants.contained}
                            size={EButtonSizes.small}
                            id="addTransaction_firearmDetails_showFRTInformation-button"
                          />
                        </Link>
                      </Grid>
                    )}
                    {registeredFirearm && (
                      <>
                        <Grid
                          size={{ xs: 12 }}
                          style={{
                            height: '1px',
                            backgroundColor: colors.primary100,
                            padding: 0,
                            margin: '20px 0 10px',
                          }}
                        />

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addTransaction.registeredFirearm.fireCycleId.label')}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(registeredFirearm.fireCycleId)}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addTransaction.registeredFirearm.serialNumber.label')}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(registeredFirearm.serialNumber)}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addTransaction.registeredFirearm.appliedMarking.label')}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(registeredFirearm.appliedOrImportMarking)}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3 }}>
                          <Stack>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {t('addTransaction.registeredFirearm.manufacturedDate.label')}
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(registeredFirearm.manufacturedAt, shortDateFormat)}
                            </Typography>
                          </Stack>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              )}
            </Accordion>
          </Grid>

          {firearmGroup && (
            <Grid size={{ xs: 12 }} sx={{ marginBottom: '20px' }}>
              <Typography
                sx={{
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '2%',
                }}
              >
                {t('addTransaction.firearmDetails.section.groupName.label')}
              </Typography>
              <Typography sx={{ fontSize: '14px;' }}>{firearmGroup.groupName}</Typography>
            </Grid>
          )}
          <Grid container>
            {Array.isArray(errors?.markings) &&
              errors?.markings.length &&
              errors?.markings[0].marking.type === 'required-min-one' && (
                <Grid size={{ xs: 12 }} sx={{ marginBottom: '20px' }}>
                  <Alert
                    text={t('transaction.validation.oneMarking.required')}
                    variant={EAlertVariants.error}
                    small
                  />
                </Grid>
              )}
          </Grid>
          {isGroupTransactionPage &&
            firearmGroup?.firearms.map((frm, index) => (
              <Grid
                size={{ xs: 12 }}
                container
                columnSpacing={2}
                sx={{ marginLeft: 0 }}
                key={`firearmGroup_${index.toString()} `}
              >
                <Grid
                  size={{ xs: 12, lg: 0.4 }}
                  sx={{
                    padding: '0 !important',
                    display: 'flex',
                    justifyContent: 'start',
                    ...(!isDesktop ? { marginBottom: '20px' } : {}),
                  }}
                >
                  <Typography
                    sx={{
                      marginLeft: '0 !important',
                      marginTop: 4,
                      fontWeight: 'bold',
                    }}
                  >
                    {`${index + 1}.`}
                  </Typography>
                </Grid>
                <Grid
                  container
                  size={{ xs: 12, lg: 11 }}
                  columnSpacing={2}
                  sx={{ padding: '0 !important' }}
                >
                  {frm.fireCycleId && (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                      <FormInput
                        name="firearmFireCycleId"
                        label={t('addTransaction.firearmDetails.section.firecycleID.label')}
                        control={control}
                        errors={errors}
                        defaultValue={frm.fireCycleId}
                        disabled
                      />
                    </Grid>
                  )}
                  {frm.serialNumber && (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                      <FormInput
                        name="firearmSerialNumber"
                        label={t('addTransaction.firearmDetails.section.serialNumber.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        defaultValue={frm.serialNumber}
                        disabled
                      />
                    </Grid>
                  )}
                  {frm.appliedMarking && (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                      <FormInput
                        name="appliedMarking"
                        label={t('addTransaction.firearmDetails.section.appliedMarking.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        defaultValue={frm.appliedMarking}
                        disabled
                      />
                    </Grid>
                  )}
                  {frm.manufacturedAt && (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                      <FormInput
                        name="firearmManufacturedDate"
                        label={t(
                          'addTransaction.firearmDetails.section.manufacturedDate.label',
                        )}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        defaultValue={formatDate(frm.manufacturedAt, shortDateFormat)}
                        disabled
                      />
                    </Grid>
                  )}
                  {frm.exhibitNumber && (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                      <FormInput
                        name="firearmExhibitNumber"
                        label={t('addTransaction.firearmDetails.section.exhibitNumber.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        defaultValue={frm.exhibitNumber}
                        disabled
                      />
                    </Grid>
                  )}
                  {isShowMarking && (
                    <>
                      {transactionTypeValue === ETransactionTypes.MARKED ? (
                        <MarkingsFormForMarked
                          items={firearmGroup?.firearms}
                          item={frm}
                          isGroupTransaction={isGroupTransactionPage}
                          control={control}
                          errors={errors}
                          required={false}
                          name={`markings[${index}].marking`}
                          setValue={setValue}
                        />
                      ) : (
                        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                          <FormInput
                            name={`markings[${index}].marking`}
                            label={getMarkingLabel()}
                            control={control}
                            errors={errors}
                            arrayName="markings"
                            arrayIndex={index}
                            fieldName="marking"
                            required={isReqiredMarking}
                          />
                        </Grid>
                      )}
                    </>
                  )}
                  {isShowYearOfConfiscation && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name={`additionalDataMap[${index}].yearOfConfiscation`}
                        label={t('addTransaction.document.section.yearOfConfiscation.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                      />
                    </Grid>
                  )}
                  {isShowYearOfTransfer && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name={`additionalDataMap[${index}].yearOfTransfer`}
                        label={t('addTransaction.document.section.yearOfTransfer.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        required
                      />
                    </Grid>
                  )}
                  {isShowSerialNumber && !frm.serialNumber && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name={`additionalDataMap[${index}].serialNumber`}
                        label={`${t('addTransaction.firearmDetails.section.serialNumber.label')}* `}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                      />
                    </Grid>
                  )}
                  {renderCustomFieldInputs(frm)}
                </Grid>
                <Grid size={{ xs: 0.6 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Link
                    href={ERouteLinks.ViewRegisteredFirearm.replace(':id', frm.uniqueId)}
                    target="_blank"
                  >
                    <Button
                      variant={EButtonVariants.outlined}
                      icon={EIconTypes.view}
                      sizePX={EButtonSizesPX.px32}
                      sx={
                        isDesktop
                          ? {
                              marginTop: '28px',
                            }
                          : {
                              marginBottom: '20px',
                            }
                      }
                    />
                  </Link>
                </Grid>
              </Grid>
            ))}
          {!withoutLocationSelect && !!transactionTypeValue && selectedNewLegalIfRequired && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addTransaction.location.section.name')} alwaysExpanded>
                <Grid container columnSpacing={2} sx={{ mb: 1, pl: 1 }}>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <FormCheckbox
                      name=""
                      id="unassignedLocation"
                      checked={unassignedLocation}
                      label={t('addTransaction.location.section.unassignedLocation.label')}
                      control={control}
                      errors={errors}
                      disabled={canChangeLegalEntity && !newFirearmLocationOwner}
                      onChange={(newState) => {
                        if (newState) setUnassignedLocationModalOpened(true);
                        else setValue('unassignedLocation', false);
                      }}
                    />
                  </Grid>
                </Grid>
                <SelectLocationTransactionPage
                  show={!unassignedLocation}
                  searchAddressesFor={newFirearmLocationOwner}
                  canSelectOnlyExistingAddress={!canSelectFromMap}
                  setValue={setValue}
                  addressId={addressId}
                />
                {!unassignedLocation && (
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      mb: 4,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      columnGap: 3,
                    }}
                  >
                    {canSelectFromMap && (
                      <Grid sx={{ flexBasis: '40%' }}>
                        <FormInput
                          name="address"
                          label={t('addFirearm.location.section.address.text')}
                          control={control}
                          errors={errors}
                          placeholder={address}
                          InputProps={{ readOnly: true }}
                          disabled
                          sx={{ minWidth: '272px' }}
                          helperText={t('addFirearm.location.section.address.helperText')}
                        />
                      </Grid>
                    )}
                    {latitude && longitude && (
                      <StyledLocationMarkConfirmation>
                        <Typography variant="body1" sx={{ color: colors.success900 }}>
                          {t('addTransaction.location.section.locationMarked.text')}
                        </Typography>
                        <StyledIconWrapper>
                          <Icon
                            type={EIconTypes.checkmark}
                            color={colors.white}
                            fontSize={14}
                          />
                        </StyledIconWrapper>
                      </StyledLocationMarkConfirmation>
                    )}
                    {canSelectFromMap && (
                      <Button
                        id="addTransaction_location_select-button"
                        onClick={() =>
                          navigate(
                            `${ERouteLinks.SelectLocation.replace(
                              ':id',
                              id,
                            )}?type=${type}&expand=firearms`,
                            {
                              state: {
                                ...routerState,
                                ...getValues(),
                                coords:
                                  latitude && longitude ? [latitude, longitude] : undefined,
                                breadcrumbs: [
                                  ...breadcrumbs,
                                  t('selectLocationOnMap.page.text'),
                                ],
                                leId: id,
                                type,
                                canSelectOnlyExistingAddress: !canSelectFromMap,
                                searchAddressesFor: newFirearmLocationOwner,
                                returnLink:
                                  isTransactionForGroup === '1'
                                    ? `${ERouteLinks.LegalEntityAddTransactionForGroup.replace(
                                        ':id',
                                        id,
                                      ).replace(
                                        ':groupId',
                                        groupId,
                                      )}?type=${type}&expand=groups`
                                    : `${ERouteLinks.MiddleTransaction.replace(':id', id)} `,
                              },
                            },
                          )
                        }
                        fullWidth={!isDesktop}
                        label={t('selectLocationOnMap.page.text')}
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        disabled={canChangeLegalEntity && !newFirearmLocationOwner}
                        sx={{ marginTop: '28px' }}
                      />
                    )}
                    {isErrorLocation && (
                      <Grid size={{ xs: 12 }}>
                        <FormErrorLabel
                          label={
                            canSelectFromMap
                              ? t(
                                  'addTransaction.location.section.selectLocationOnMap.errorLabel',
                                )
                              : t('addTransaction.location.section.selectLocation.errorLabel')
                          }
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </Accordion>
            </Grid>
          )}
          {isShowCountryOfImport && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addFirearm.countryOfImport.section.name')} alwaysExpanded>
                <Grid container columnSpacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormInput
                      name="importer"
                      label={t('addFirearm.countryOfImport.section.importer.label.validation')}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 3 }}>
                    <FormSelect
                      options={mapDictionaryToOptionProp(countryAll)}
                      name="countryOfImportUniqueId"
                      setValue={setValue}
                      label={t('addFirearm.countryOfImport.section.country.label.validation')}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </Accordion>
            </Grid>
          )}
          {isShowCountryOfExport && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addFirearm.countryOfExport.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormSelect
                      options={mapDictionaryToOptionProp(countryAll)}
                      name="countryOfExportUniqueId"
                      label={t('addFirearm.countryOfExport.section.country.label.validation')}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}
          {isShowMarking && isMiddleTransactionPage && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addFirearm.marking.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    {transactionTypeValue === ETransactionTypes.MARKED ? (
                      <MarkingsFormForMarked
                        item={registeredFirearm}
                        isGroupTransaction={isGroupTransactionPage}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        name={`marking.marking`}
                      />
                    ) : (
                      <FormInput
                        name="marking.marking"
                        label={getMarkingLabel()}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        required={isReqiredMarking}
                      />
                    )}
                    {isShowYearOfConfiscation && (
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <FormInput
                          name="additionalData.yearOfConfiscation"
                          label={t('addTransaction.document.section.yearOfConfiscation.label')}
                          setValue={setValue}
                          control={control}
                          errors={errors}
                        />
                      </Grid>
                    )}
                    {isShowYearOfTransfer && (
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <FormInput
                          name="additionalData.yearOfTransfer"
                          label={t('addTransaction.document.section.yearOfTransfer.label')}
                          setValue={setValue}
                          control={control}
                          errors={errors}
                          required
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}
          {isShowSerialNumber &&
            isMiddleTransactionPage &&
            registeredFirearm &&
            !registeredFirearm.serialNumber && (
              <Grid size={{ xs: 12 }}>
                <Accordion
                  title={t('addTransaction.document.section.serialNumber.label')}
                  alwaysExpanded
                >
                  <Box sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <FormInput
                          name="additionalData.serialNumber"
                          label={t('addTransaction.document.section.serialNumber.label')}
                          setValue={setValue}
                          control={control}
                          errors={errors}
                          required
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Accordion>
              </Grid>
            )}
          {isShowExpirationDate && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addTransaction.expiration.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormDatePicker
                      name="expirationDate"
                      label={t(
                        'addTransaction.expiration.section.expirationDate.label.validation',
                      )}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}
          {isShowExpirationTime && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addTransaction.expiration.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormDateTimePicker
                      name="expirationTime"
                      label={`${t(
                        'addTransaction.expiration.section.expirationTime.label.validation',
                      )}* `}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}

          {isModificationDescription && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('firearms.transaction.modification.label')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12 }}>
                    <FormWyswigEditor
                      name="description"
                      label={`${t('firearms.transaction.modification.label')}* `}
                      placeholder={t('firearms.transaction.modification.label')}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      charsLimit={1000}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addTransaction.document.section.name')} alwaysExpanded>
              <Box>
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                      <Alert
                        text={t('general.field.availableAttachmentFormats.text')}
                        variant={EAlertVariants.warning}
                      />
                    </Box>
                  </Grid>
                  {attachments?.fields?.map((attachment: any, index) => (
                    <Grid
                      key={attachment.id}
                      container
                      size={{ xs: 12 }}
                      sx={{ mb: !isDesktop ? 2 : 0 }}
                    >
                      <Grid size={{ xs: attachments.fields?.length > 1 ? 11 : 12, lg: 6 }}>
                        <FormInput
                          name={`attachments[${index}].attachmentName`}
                          defaultValue={
                            attachment?.file?.name
                              ? removeFileExtension(attachment?.file?.name)
                              : ''
                          }
                          label={t('addTransaction.document.section.attachmentName.label')}
                          control={control}
                          errors={errors}
                          arrayName="attachments"
                          arrayIndex={index}
                          fieldName="attachmentName"
                          InputProps={{ disabled: attachment?.file === null }}
                        />
                      </Grid>
                      {!isDesktop && renderRemoveAttachmentButton(index)}
                      <Grid
                        size={{ xs: 12, lg: 3 }}
                        sx={{ marginTop: { lg: '28px' }, ml: { lg: 2 } }}
                      >
                        <Button
                          isUploadButton
                          onClick={(files) => handleUploadFile(files[0], index)}
                          fullWidth
                          label={t('addTransaction.document.section.browseFiles.button')}
                          id="add_file-button"
                          icon={EIconTypes.plus}
                          size={EButtonSizes.small}
                          isMultiple
                        />
                        {errors && errors.attachments && getAttachmentError(errors, index) && (
                          <FormErrorLabel
                            label={getAttachmentError(errors, index)?.message ?? ''}
                          />
                        )}
                      </Grid>
                      {isDesktop && renderRemoveAttachmentButton(index)}
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12, lg: 3 }} sx={{ mt: { xs: 2, lg: 0 } }}>
                    <Button
                      onClick={() =>
                        attachments.append({
                          attachmentName: '',
                          file: null,
                        })
                      }
                      fullWidth
                      label={t('addFirearm.document.section.addFile.button')}
                      id="add_file-button"
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Accordion>
          </Grid>
          <Grid container size={{ xs: 12 }} sx={{ mt: 4, justifyContent: 'flex-start' }}>
            <Grid size={{ xs: 12, lg: 6 }} container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  onClick={onSubmit}
                  fullWidth
                  isLoading={submitBlocked}
                  disabled={!isCurrentOwnerLoaded || !isCurrentKeeperLoaded}
                  label={t('addTransaction.save.button')}
                  variant={EButtonVariants.contained}
                  sx={{ mr: 1 }}
                  id="addTransaction-save-button"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  id="addTransaction-cancel-button"
                  fullWidth
                  label={t('addTransaction.cancel.button')}
                  variant={EButtonVariants.outlined}
                  onClick={onCancel}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
            {createTransactionError && (
              <Box sx={{ mb: 2 }}>
                <Alert text={createTransactionError} variant={EAlertVariants.error} />
              </Box>
            )}
          </Grid>
        </Grid>
      </div>
      <FormModal
        open={unassignedLocationModalOpened}
        onClickClose={() => {
          setUnassignedLocationModalOpened(false);
          setValue('unassignedLocation', false);
        }}
        onClickApprove={() => {
          setValue('unassignedLocation', true);
          setUnassignedLocationModalOpened(false);
        }}
        title={t('transaction.unassigned.modal.title')}
        text={t('transaction.middle.unassigned.modal.text')}
        approveButtonLabel="general.confirm.label"
      />
    </>
  );
};

export { LegalEntityAddTransactionForGroupPage };
